import { Modal } from '@/components/admin/Modal';
import { CopyButton } from '@/components/ui/copy-button';
import { cn } from '@/lib/cn';
import { copySvgAsPng, readThemeSurface } from '@/lib/svg-image';
import { Check, ImageDown, Maximize2, Minimize2, ZoomIn, ZoomOut } from 'lucide-react';
import { useCallback, useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  /** 정규화된 SVG 문자열. */
  svg: string;
  /** 원본 mermaid 소스 (소스 복사용). */
  source: string;
  title?: string;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

/**
 * mermaid 다이어그램 확대 뷰어. 모달(`size="xl"`) ↔ 전체화면(`size="full"`) 토글, 줌 ±,
 * 이미지/소스 복사. 본문은 자연 크기 SVG 를 스크롤 컨테이너에 두고 CSS scale 로 확대.
 */
export function MermaidViewerModal({ open, onClose, svg, source, title = '다이어그램' }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [copied, setCopied] = useState(false);

  const handleCopyImage = useCallback(async () => {
    const ok = await copySvgAsPng(svg, { background: readThemeSurface() });
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }, [svg]);

  const iconBtn =
    'grid size-7 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size={expanded ? 'full' : 'xl'}
      titleActions={
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() =>
              setZoom((z) => Math.max(MIN_ZOOM, Math.round((z - ZOOM_STEP) * 100) / 100))
            }
            disabled={zoom <= MIN_ZOOM}
            aria-label="축소"
            title="축소"
            className={iconBtn}
          >
            <ZoomOut className="size-4" />
          </button>
          <span className="w-10 text-center text-xs tabular-nums text-foreground-muted">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() =>
              setZoom((z) => Math.min(MAX_ZOOM, Math.round((z + ZOOM_STEP) * 100) / 100))
            }
            disabled={zoom >= MAX_ZOOM}
            aria-label="확대"
            title="확대"
            className={iconBtn}
          >
            <ZoomIn className="size-4" />
          </button>
          <span className="mx-1 h-4 w-px bg-border" />
          <button
            type="button"
            onClick={handleCopyImage}
            aria-label="이미지 복사"
            title={copied ? '복사됨' : '이미지 복사'}
            className={iconBtn}
          >
            {copied ? <Check className="size-4 text-accent" /> : <ImageDown className="size-4" />}
          </button>
          <CopyButton text={source} label="소스 복사" size="sm" />
          <span className="mx-1 h-4 w-px bg-border" />
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? '모달 크기로' : '전체화면'}
            title={expanded ? '모달 크기로' : '전체화면'}
            className={iconBtn}
          >
            {expanded ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
          </button>
        </div>
      }
    >
      <div
        className={cn(
          'overflow-auto rounded-md bg-surface',
          expanded ? 'h-[calc(100dvh-9rem)]' : 'h-[70vh]',
        )}
      >
        <div
          className="inline-block p-4 [&_svg]:h-auto [&_svg]:max-w-none"
          style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: mermaid.render 가 생성한 SVG.
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
    </Modal>
  );
}
